import { Spinner, useToast } from '@chakra-ui/react'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SumsubWebSdk from '@sumsub/websdk-react'
import type React from 'react'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import PageWrapper from '../../../../components/Pagewrapper'
import Toast from '../../../../components/Toast'
import {
  coreApi,
  useGetWalletScreeningAccessTokenQuery,
} from '../../../../redux/services/coreApi'
import type { RootState } from '../../../../redux/store'

const WalletVerifyScreen: React.FC = () => {
  const toast = useToast()
  const history = useHistory()
  const dispatch = useDispatch()
  const { walletId } = useParams<{ walletId: string }>()
  const userInfo = useSelector((state: RootState) => state.session.user)

  const { data, isLoading, error } = useGetWalletScreeningAccessTokenQuery({
    userId: userInfo.id,
    walletId,
  })

  const navigateBack = useCallback(() => {
    dispatch(coreApi.util.invalidateTags(['Wallets']))
    history.push('/app/wallets')
  }, [dispatch, history])

  const expirationHandler = useCallback(() => {
    toast({
      position: 'bottom-right',
      render: () => (
        <Toast type="error">
          Session expired. Please go back and try again.
        </Toast>
      ),
    })
  }, [toast])

  const messageHandler = useCallback(
    (
      event: string,
      payload?: {
        reviewResult?: { reviewAnswer: 'GREEN' | 'RED' }
      }
    ) => {
      if (event === 'idCheck.onApplicantSubmitted') {
        toast({
          position: 'bottom-right',
          render: () => (
            <Toast type="success">
              Verification submitted! Your wallet is being reviewed.
            </Toast>
          ),
        })
        navigateBack()
      }

      if (event === 'idCheck.onApplicantStatusChanged') {
        if (payload?.reviewResult?.reviewAnswer === 'GREEN') {
          toast({
            position: 'bottom-right',
            render: () => (
              <Toast type="success">Wallet verified successfully!</Toast>
            ),
          })
          navigateBack()
        } else if (payload?.reviewResult?.reviewAnswer === 'RED') {
          toast({
            position: 'bottom-right',
            render: () => (
              <Toast type="error">
                Wallet verification was not approved.
              </Toast>
            ),
          })
          navigateBack()
        }
      }
    },
    [toast, navigateBack]
  )

  const errorHandler = useCallback(() => {
    toast({
      position: 'bottom-right',
      render: () => (
        <Toast type="error">Verification encountered an error.</Toast>
      ),
    })
  }, [toast])

  return (
    <PageWrapper
      title="Verify wallet ownership"
      description="Complete the verification to activate your wallet"
      fixedWidth
    >
      <button
        onClick={navigateBack}
        className="flex items-center gap-2 text-description hover:text-brand-accent mb-6"
      >
        <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
        Back to wallets
      </button>

      {isLoading && (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <div className="card p-6 text-center">
          <p className="text-red-600 dark:text-red-400">
            Failed to load verification. The wallet may not have a pending
            screening.
          </p>
          <button onClick={navigateBack} className="button mt-4">
            Back to wallets
          </button>
        </div>
      )}

      {data?.accessToken && (
        <div className="card p-0 overflow-hidden">
          <SumsubWebSdk
            accessToken={data.accessToken}
            expirationHandler={expirationHandler}
            onMessage={messageHandler}
            onError={errorHandler}
          />
        </div>
      )}
    </PageWrapper>
  )
}

export default WalletVerifyScreen
